import type { Type } from '@angular/core';

export interface ContractReference {
  readonly code: string;
  readonly version: number;
}
export interface BlockReference extends ContractReference {
  readonly provider: string;
}
export type BlockContextKey =
  | 'personId'
  | 'hearingId'
  | 'proceedingId'
  | 'sittingId'
  | 'workflowInstanceId'
  | 'stepOccurrenceId';
export type WorkspaceWidth = 'full' | 'half';
export type BlockHost = 'step-shell' | 'case-context' | 'dashboard-expansion';
export type BlockRegion = 'main' | 'supporting';
export interface BlockPlacement {
  readonly host: BlockHost;
  readonly region: BlockRegion;
}

type SettingBase = { readonly required: boolean };
export type BlockSetting = SettingBase &
  (
    | { readonly type: 'string'; readonly maxLength: number }
    | { readonly type: 'boolean' }
    | {
        readonly type: 'integer';
        readonly minimum: number;
        readonly maximum: number;
      }
    | { readonly type: 'enum'; readonly values: readonly string[] }
  );
export interface BlockSettingsSchema extends ContractReference {
  readonly fields: Readonly<Record<string, BlockSetting>>;
}
type SettingValue<T extends BlockSetting> = T extends { type: 'boolean' }
  ? boolean
  : T extends { type: 'integer' }
    ? number
    : T extends { type: 'enum'; values: readonly (infer V)[] }
      ? V
      : string;
export type BlockSettings<S extends BlockSettingsSchema> = {
  readonly [K in keyof S['fields'] as S['fields'][K]['required'] extends true
    ? K
    : never]: SettingValue<S['fields'][K]>;
} & {
  readonly [K in keyof S['fields'] as S['fields'][K]['required'] extends true
    ? never
    : K]?: SettingValue<S['fields'][K]>;
};

export interface BlockRead extends ContractReference {
  readonly parameterContract?: ContractReference;
  readonly dataContract: ContractReference;
  readonly parameters: Readonly<Record<string, BlockContextKey>>;
  /** Explicit scalar options supplied on demand by this renderer, never context identities. */
  readonly requestParameters?: readonly string[];
}
export interface BlockManifest extends BlockReference {
  readonly name: string;
  readonly description: string;
  readonly category: string;
  readonly keywords: readonly string[];
  readonly contextNeeds: readonly BlockContextKey[];
  readonly reads: readonly BlockRead[];
  readonly settingsSchema: BlockSettingsSchema;
  readonly placements: readonly BlockPlacement[];
  readonly layout: {
    readonly widths: readonly WorkspaceWidth[];
    readonly minimumWidthPx: number;
    readonly minimumHeightPx: number;
    readonly responsive: 'stack-in-order';
  };
  readonly instances: 'single-per-workspace' | 'repeatable';
  readonly deprecated?: string;
}
export interface BlockRegistration {
  readonly manifest: BlockManifest;
  readonly load: () => Promise<Type<unknown>>;
}
export interface WorkspaceBlock {
  readonly instanceId: string;
  readonly block: BlockReference;
  readonly settingsVersion: number;
  readonly settings: unknown;
  readonly placement: BlockPlacement;
  readonly width: WorkspaceWidth;
  /** Optional main-page section; hidden sections keep their mounted editor state. */
  readonly tabId?: string;
}
export interface BlockEnvironment {
  readonly host: BlockHost;
  readonly contextKeys: readonly BlockContextKey[];
  /** Width in CSS pixels of each region, after shell rails/padding. */
  readonly regionWidths: Readonly<Partial<Record<BlockRegion, number>>>;
  readonly layoutProfile: ContractReference;
}
export interface BlockDiagnostic {
  readonly instanceId: string;
  readonly code: string;
  readonly message: string;
}
export const TWO_COLUMN_WORKSPACE = Object.freeze({
  code: 'workspace.two-column',
  version: 1,
  tracks: 12,
  full: 12,
  half: 6,
  gapPx: 16,
  halfRowMinimumPx: 768,
} as const);
